"use client";

import { type UserCreditTransactionVo } from "@/api";
import CloseIcon from "@/assets/icons/popover-close.svg";
import GreyLogo from "@/assets/images/grey-logo.svg";
import {
  useCreditTransactions,
  type CreditTransactionMode,
} from "@/hooks/useCreditTransactions";
import { formatTime } from "@/util/datetime";
import { formatNumberWithCommas } from "@/util/formatNumber";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ConfigProvider, Modal, Pagination, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Tooltip } from "../Tooltip";
import styles from "./index.module.css";

export interface CreditUsageModalProps {
  open: boolean;
  onClose: () => void;
  mode: CreditTransactionMode;
  threadId?: string;
}

const columns: ColumnsType<UserCreditTransactionVo> = [
  {
    title: "Description",
    dataIndex: "reasonSubType",
    key: "description",
    width: "25%",
    render: (reasonSubType: string, record: UserCreditTransactionVo) => {
      const description = reasonSubType
        ?.split("_")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");

      const productName = (record.productName || "").split("_").join(" ");

      if (!productName) {
        return description;
      }

      return (
        <p className="flex items-center gap-2">
          <span>{description}</span>
          <Tooltip theme="light" title={productName}>
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </p>
      );
    },
  },
  {
    title: "Direction",
    dataIndex: "direction",
    key: "direction",
    width: "25%",
    render: (direction: string) => {
      if (direction === "IN") return "Acquired";
      if (direction === "OUT") return "Consumed";
      return direction;
    },
  },
  {
    title: "Date",
    dataIndex: "createTime",
    key: "createTime",
    width: "25%",
    render: (date: string) => {
      if (!date) return "-";
      return formatTime(date, "DD MMM YYYY, HH:mm:ss");
    },
  },
  {
    title: "Credits Change",
    dataIndex: "amount",
    key: "amount",
    width: "25%",
    align: "right",
    render: (amount: number, record: UserCreditTransactionVo) => {
      if (amount === undefined || amount === null) return "-";
      const formatted = formatNumberWithCommas(Math.abs(amount));
      const sign = record.direction === "IN" ? "+" : "-";
      return `${sign}${formatted}`;
    },
  },
];

const TITLE_MAP: Record<CreditTransactionMode, string> = {
  thread: "Credit usage details of current project",
  account: "Credit usage details",
};

export function CreditUsageModal({
  open,
  onClose,
  mode,
  threadId,
}: CreditUsageModalProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { loading, data, total } = useCreditTransactions({
    mode,
    threadId,
    pageNum: currentPage,
    pageSize,
    enabled: open,
  });

  // Reset pagination when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [open]);

  const dataSource = data.map((item, index) => ({
    ...item,
    key: `${item.createTime}-${index}`,
  }));

  return (
    <ConfigProvider
      theme={{
        components: {
          Pagination: {
            itemActiveBg: "rgba(255, 255, 255, 0.08)",
            itemBg: "rgba(255, 255, 255, 0.04)",
            lineWidth: 0,
            colorText: "rgba(255, 255, 255, 0.60)",
            colorPrimary: "rgb(255, 255, 255)",
            itemLinkBg: "#fff",
            colorPrimaryHover: "rgb(255, 255, 255)",
          },
          Table: {
            headerBg: "rgba(255, 255, 255, 0.08)",
            colorBgContainer: "transparent",
            cellPaddingBlock: 13,
            fontSize: 14,
            colorText: "rgba(255, 255, 255, 0.6)",
            borderColor: "rgba(255, 255, 255, 0.12)",
            borderRadius: 12,
            colorBorder: "rgba(255, 255, 255, 0.12)",
            lineWidth: 0.5,
          },
        },
      }}
      renderEmpty={() => (
        <div className="flex flex-col items-center justify-center gap-2 h-30">
          <GreyLogo className="w-12 h-12" />
          <p>No data</p>
        </div>
      )}
    >
      <Modal
        open={open}
        onCancel={onClose}
        centered
        title={null}
        footer={null}
        closeIcon={null}
        maskClosable={true}
        width={800}
        classNames={{
          content: styles.modalContent,
        }}
      >
        <div className={styles.container}>
          <CloseIcon
            className="absolute top-5 right-5 w-5 h-5 text-white/40 cursor-pointer"
            onClick={onClose}
            aria-label="Close"
          />

          <h2 className="text-text-primary font-semibold text-base leading-snug">
            {TITLE_MAP[mode]}
          </h2>

          <div
            className={
              "mt-6 rounded-xl overflow-hidden border-[0.5px] border-solid border-white/12"
            }
          >
            <Table
              columns={columns}
              dataSource={dataSource}
              loading={loading}
              pagination={false}
              bordered={false}
              rowHoverable={false}
              className={styles.table}
              rootClassName={styles.root}
            />
          </div>

          {total > 0 && (
            <div className="mt-6 flex justify-center">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
      </Modal>
    </ConfigProvider>
  );
}

export default CreditUsageModal;
